/**
 * StylizedSidebarCalloutEditor - Editor component for Stylized Sidebar Callout Block
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 7.8, 7.9, 7.10
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import type { MouseEvent } from 'react';

import type { JSONContent } from '@tiptap/core';
import {
  isRichTextContentEmpty,
  useTheme,
  type SidebarCalloutTitleLevel,
  type StylizedSidebarCalloutBlock,
} from '@course-authoring/shared-types';
import {
  createSidebarCalloutStyles,
  useIsMobileViewport,
} from '@course-authoring/ui-blocks';

import { StylizedSidebarCalloutFieldEditor } from './StylizedSidebarCalloutFieldEditor';
import {
  createStylizedSidebarCalloutEditorStyles,
  stylizedSidebarCalloutEditorInnerStyles,
} from './stylizedSidebarCalloutEditorStyles';
import type { StylizedSidebarCalloutFieldName } from './stylizedSidebarCalloutEditorTypes';
import { TitleLevelSelector } from './TitleLevelSelector';

export interface StylizedSidebarCalloutEditorProps {
  block: StylizedSidebarCalloutBlock;
  onChange: (updates: Partial<StylizedSidebarCalloutBlock>) => void;
  autoFocus?: boolean;
  translationMode?: boolean;
}

export function StylizedSidebarCalloutEditor({
  block,
  onChange,
  autoFocus = false,
  translationMode = false,
}: StylizedSidebarCalloutEditorProps): React.ReactElement {
  const { tokens } = useTheme();
  const isMobile = useIsMobileViewport(tokens.layout.mobileBreakpoint);
  const [focusedField, setFocusedField] = useState<StylizedSidebarCalloutFieldName | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const titleLevel = block.titleLevel ?? 'h2';
  const sharedStyles = useMemo(
    () => createSidebarCalloutStyles(tokens, isMobile, titleLevel),
    [tokens, isMobile, titleLevel]
  );

  const isFocused = focusedField !== null;
  const editorStyles = useMemo(
    () => createStylizedSidebarCalloutEditorStyles(tokens, isFocused),
    [isFocused, tokens]
  );
  const hasTitle = !isRichTextContentEmpty(block.title);
  const hasBody = !isRichTextContentEmpty(block.body);
  const hasCalloutTitle = !isRichTextContentEmpty(block.calloutTitle);
  const hasCalloutBody = !isRichTextContentEmpty(block.calloutBody);
  const shouldCollapseMainBodySpacing = !hasTitle && hasBody;
  const shouldCenterCalloutBody = !hasCalloutTitle && hasCalloutBody;

  const editorMinHeights = useMemo(
    () => ({
      title:
        titleLevel === 'h1'
          ? isMobile
            ? '72px'
            : '96px'
          : titleLevel === 'h3'
            ? isMobile
              ? '48px'
              : '56px'
            : isMobile
              ? '60px'
              : '72px',
      body: isMobile ? '28px' : '32px',
      calloutTitle: '48px',
      calloutBody: isMobile ? '28px' : '32px',
    }),
    [isMobile, titleLevel]
  );

  const handleFieldChange = useCallback(
    (fieldName: StylizedSidebarCalloutFieldName, content: JSONContent) => {
      onChange({ [fieldName]: content });
    },
    [onChange]
  );

  const handleFieldFocus = useCallback((fieldName: StylizedSidebarCalloutFieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleFieldBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleTitleLevelChange = useCallback(
    (nextTitleLevel: SidebarCalloutTitleLevel) => {
      onChange({ titleLevel: nextTitleLevel });
    },
    [onChange]
  );

  const handleButtonMouseDown = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  }, []);

  return (
    <div ref={containerRef} style={editorStyles.container}>
      <style>{stylizedSidebarCalloutEditorInnerStyles}</style>
      <div style={sharedStyles.layout}>
        <div style={sharedStyles.mainContent}>
          {!translationMode ? <TitleLevelSelector
            currentLevel={titleLevel}
            onSelectLevel={(level) => handleTitleLevelChange(level as SidebarCalloutTitleLevel)}
            tokens={tokens}
          /> : null}

          <div>
            <StylizedSidebarCalloutFieldEditor
            content={block.title}
            placeholder="Headline..."
            isFocused={focusedField === 'title'}
            onFocus={() => handleFieldFocus('title')}
            onBlur={handleFieldBlur}
            onContentChange={(content) => handleFieldChange('title', content)}
            autoFocus={autoFocus}
            containerRef={containerRef}
            fieldContainerStyle={editorStyles.fieldContainer}
            editorStyle={sharedStyles.title}
            editorClassName="ssc-editor--title"
            minHeight={editorMinHeights.title}
            hideToolbar
            />
          </div>
          <div>
            <StylizedSidebarCalloutFieldEditor
            content={block.body}
            placeholder="Body text..."
            allowLists={true}
            isFocused={focusedField === 'body'}
            onFocus={() => handleFieldFocus('body')}
            onBlur={handleFieldBlur}
            onContentChange={(content) => handleFieldChange('body', content)}
            autoFocus={false}
            containerRef={containerRef}
            fieldContainerStyle={editorStyles.fieldContainer}
            editorStyle={{
              ...sharedStyles.body,
              marginTop: shouldCollapseMainBodySpacing ? 0 : sharedStyles.body.marginTop,
            }}
            editorClassName="ssc-editor--body"
            minHeight={editorMinHeights.body}
            />
          </div>
        </div>

        <div
          style={{
            ...sharedStyles.sidebar,
            ...(shouldCenterCalloutBody ? sharedStyles.sidebarCentered : {}),
          }}
        >
          <div>
            <StylizedSidebarCalloutFieldEditor
            content={block.calloutTitle}
            placeholder="Title..."
            isFocused={focusedField === 'calloutTitle'}
            onFocus={() => handleFieldFocus('calloutTitle')}
            onBlur={handleFieldBlur}
            onContentChange={(content) => handleFieldChange('calloutTitle', content)}
            autoFocus={false}
            containerRef={containerRef}
            fieldContainerStyle={editorStyles.fieldContainer}
            editorStyle={sharedStyles.calloutTitle}
            editorClassName="ssc-editor--callout-title"
            minHeight={editorMinHeights.calloutTitle}
            defaultBold
            />
          </div>
          <div>
            <StylizedSidebarCalloutFieldEditor
            content={block.calloutBody}
            placeholder="Text..."
            allowLists={true}
            isFocused={focusedField === 'calloutBody'}
            onFocus={() => handleFieldFocus('calloutBody')}
            onBlur={handleFieldBlur}
            onContentChange={(content) => handleFieldChange('calloutBody', content)}
            autoFocus={false}
            containerRef={containerRef}
            fieldContainerStyle={editorStyles.fieldContainer}
            editorStyle={{
              ...sharedStyles.calloutBody,
              marginTop: shouldCenterCalloutBody ? 0 : sharedStyles.calloutBody.marginTop,
            }}
            editorClassName="ssc-editor--callout-body"
            minHeight={editorMinHeights.calloutBody}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

StylizedSidebarCalloutEditor.displayName = 'StylizedSidebarCalloutEditor';
